import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useCms } from "../CmsContext";
import { EditorLayout } from "../EditorChrome";
import { FieldForValue, TextField, ToggleField, ImageField } from "../Fields";
import { deepClone, deepEqual, getCollection, slugify, formatTime } from "../cmsUtils";

const TEAM_DEPARTMENTS = ["Advisory", "Convene", "Growth and GTM", "Ignite", "Leadership", "Operations"];
const PORTFOLIO_SERVICES = [
  { value: "growth-gtm", label: "Growth & GTM" },
  { value: "advisory", label: "Advisory" },
  { value: "convene", label: "Convene" },
];
const PORTFOLIO_EXPERTISE = [
  "Consumer Digital Engagement",
  "Cybersecurity",
  "Industrial Tech",
  "Enterprise IT",
  "Social Impact",
];

const NEW_TEMPLATES = {
  team: () => ({
    slug: "",
    name: "",
    role: "",
    departments: [],
    photo: "",
    email: null,
    bio: [""],
    personalQuote: null,
    funFact: null,
    linkedin: null,
    order: 0,
  }),
  newsroom: () => ({
    slug: "",
    title: "",
    date: new Date().toISOString(),
    hero: "",
    excerpt: "",
    bodyHtml: "",
    author: "Landmark Ventures",
  }),
  portfolio: () => ({
    id: Date.now(),
    name: "",
    blurb: null,
    logo: null,
    services: [],
    expertise: [],
    locations: [],
    website: null,
    deal: null,
    order: 0,
  }),
  caseStudies: () => ({
    slug: "",
    client: "",
    service: "",
    oneLiner: "",
    tactics: [],
    website: null,
    heroImage: null,
    sections: [{ title: "", blocks: [{ type: "p", text: "" }] }],
    results: [],
  }),
  caseCards: () => ({
    id: Date.now(),
    title: "",
    blurb: "",
    logo: "",
    slug: "",
  }),
};

const labelFor = (collectionKey, payload) =>
  payload.name || payload.title || payload.client || payload.slug || "";

const slugSourceFor = (collectionKey, payload) =>
  payload.name || payload.title || payload.client || "";

function CheckboxGroup({ label, options, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  return (
    <div className="lw-field">
      <label className="lw-field__label">{label}</label>
      <div className="lw-checkgroup">
        {options.map((opt) => {
          const optValue = typeof opt === "string" ? opt : opt.value;
          const optLabel = typeof opt === "string" ? opt : opt.label;
          const checked = selected.includes(optValue);
          return (
            <label key={optValue} className={`lw-check ${checked ? "lw-check--on" : ""}`}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() =>
                  onChange(
                    checked ? selected.filter((v) => v !== optValue) : [...selected, optValue]
                  )
                }
              />
              {optLabel}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function DateField({ label, value, onChange }) {
  const dateOnly = value ? String(value).slice(0, 10) : "";
  return (
    <div className="lw-field">
      <label className="lw-field__label">{label}</label>
      <input
        className="lw-input lw-input--date"
        type="date"
        value={dateOnly}
        onChange={(e) => onChange(e.target.value ? `${e.target.value}T12:00:00+00:00` : "")}
      />
    </div>
  );
}

function DealField({ value, onChange }) {
  const has = value != null;
  return (
    <div className="lw-group">
      <p className="lw-group__label">Deal / exit</p>
      <div className="lw-group__body">
        <ToggleField
          label="This company has an announced deal (shown as a deal card)"
          value={has}
          onChange={(v) =>
            onChange(v ? { value: "Undisclosed", label: "Acquired by", acquirerLogo: null } : null)
          }
        />
        {has && (
          <>
            <TextField
              label="Deal value"
              value={value.value}
              onChange={(v) => onChange({ ...value, value: v })}
              hint="e.g. $120M or Undisclosed"
            />
            <TextField
              label="Deal label"
              value={value.label}
              onChange={(v) => onChange({ ...value, label: v })}
              hint="e.g. Acquired by, Merged with"
            />
            <ImageField
              label="Acquirer logo"
              value={value.acquirerLogo}
              onChange={(v) => onChange({ ...value, acquirerLogo: v || null })}
            />
          </>
        )}
      </div>
    </div>
  );
}

// per-collection custom widgets for specific keys
const FIELD_OVERRIDES = {
  team: {
    departments: (value, onChange) => (
      <CheckboxGroup label="Departments" options={TEAM_DEPARTMENTS} value={value} onChange={onChange} />
    ),
  },
  newsroom: {
    date: (value, onChange) => <DateField label="Publish date" value={value} onChange={onChange} />,
  },
  portfolio: {
    services: (value, onChange) => (
      <CheckboxGroup label="Services" options={PORTFOLIO_SERVICES} value={value} onChange={onChange} />
    ),
    expertise: (value, onChange) => (
      <CheckboxGroup label="Expertise" options={PORTFOLIO_EXPERTISE} value={value} onChange={onChange} />
    ),
    deal: (value, onChange) => <DealField value={value} onChange={onChange} />,
  },
};

export function EntryEditorPage({ isNew = false }) {
  const { collectionKey, slug: slugParam } = useParams();
  const collection = getCollection(collectionKey);
  const navigate = useNavigate();
  const { api, API_BASE, refreshPublishStatus, bumpPreview } = useCms();

  const originalSlug = isNew ? "" : decodeURIComponent(slugParam);
  const [payload, setPayload] = useState(null);
  const [savedPayload, setSavedPayload] = useState(null);
  const [slug, setSlug] = useState("");
  const [savedSlug, setSavedSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [entryMeta, setEntryMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (isNew) {
      const template = (NEW_TEMPLATES[collectionKey] || (() => ({})))();
      setPayload(template);
      setSavedPayload(null);
      setSlug("");
      setSavedSlug("");
      setSlugTouched(false);
      return;
    }
    (async () => {
      const res = await api.get(
        `${API_BASE}/entries/${collectionKey}/${encodeURIComponent(originalSlug)}`
      );
      if (res && res.success) {
        const draft = deepClone(res.entry.draft || {});
        setEntryMeta(res.entry);
        setPayload(draft);
        setSavedPayload(deepClone(draft));
        setSlug(res.entry.slug);
        setSavedSlug(res.entry.slug);
      } else {
        setLoadError(res?.error || "Failed to load entry");
      }
    })();
  }, [api, API_BASE, collectionKey, originalSlug, isNew]);

  // auto-slug while creating, until the user edits the slug manually
  useEffect(() => {
    if (!isNew || slugTouched || !payload) return;
    const source = slugSourceFor(collectionKey, payload);
    if (source) setSlug(slugify(source));
  }, [payload, isNew, slugTouched, collectionKey]);

  const dirty =
    payload != null && (!deepEqual(payload, savedPayload) || slug !== savedSlug);

  const save = useCallback(async () => {
    if (!payload) return;
    if (!slug) {
      toast.error("A slug is required");
      return;
    }
    const finalPayload = { ...payload };
    if ("slug" in finalPayload) finalPayload.slug = slug;

    setSaving(true);
    const res = await api.post(`${API_BASE}/entries/save`, {
      collectionKey,
      slug,
      originalSlug: isNew ? undefined : savedSlug || originalSlug,
      draft: finalPayload,
      label: labelFor(collectionKey, finalPayload),
    });
    setSaving(false);

    if (res && res.success) {
      toast.success(isNew ? `${collection.singular} created (draft)` : "Draft saved");
      refreshPublishStatus();
      bumpPreview();
      if (isNew || slug !== savedSlug) {
        navigate(`../collections/${collectionKey}/${encodeURIComponent(slug)}`, { replace: true });
      }
      setPayload(finalPayload);
      setSavedPayload(deepClone(finalPayload));
      setSavedSlug(slug);
      setEntryMeta(res.entry);
    } else {
      toast.error(res?.error || "Save failed");
    }
  }, [
    api,
    API_BASE,
    collectionKey,
    payload,
    slug,
    savedSlug,
    originalSlug,
    isNew,
    collection,
    navigate,
    refreshPublishStatus,
    bumpPreview,
  ]);

  const overrides = FIELD_OVERRIDES[collectionKey] || {};
  const fieldEntries = useMemo(
    () => Object.entries(payload || {}).filter(([key]) => !["order", "id", "slug"].includes(key)),
    [payload]
  );

  if (!collection) return <p className="lw-empty">Unknown collection.</p>;
  if (loadError) return <p className="lw-empty">{loadError}</p>;
  if (!payload) return <p className="lw-empty">Loading…</p>;

  const title = isNew
    ? `New ${collection.singular.toLowerCase()}`
    : labelFor(collectionKey, payload) || originalSlug;

  return (
    <EditorLayout
      backTo={`../collections/${collectionKey}`}
      backLabel={collection.label}
      title={title}
      meta={
        entryMeta?.updatedTime
          ? `Last edited ${formatTime(entryMeta.updatedTime)}${
              entryMeta.updatedBy?.fullName ? ` by ${entryMeta.updatedBy.fullName}` : ""
            }`
          : undefined
      }
      dirty={dirty}
      saving={saving}
      onSave={save}
      sitePath={!isNew ? collection.sitePath(slug) : undefined}
    >
      <div className="lw-fields">
        <TextField
          label="Slug"
          value={slug}
          onChange={(v) => {
            setSlugTouched(true);
            setSlug(slugify(v) || v);
          }}
          hint={`URL identifier — ${collection.sitePath(slug || "…")}`}
        />
        {fieldEntries.map(([key, value]) =>
          overrides[key] ? (
            <React.Fragment key={key}>
              {overrides[key](value, (next) => setPayload({ ...payload, [key]: next }))}
            </React.Fragment>
          ) : (
            <FieldForValue
              key={key}
              fieldKey={key}
              value={value}
              onChange={(next) => setPayload({ ...payload, [key]: next })}
            />
          )
        )}
      </div>
    </EditorLayout>
  );
}
